import {
    Account,
    BaseTask,
    BaseZenputCollection,
    BaseZenputModel,
    FormStructure,
    Paginator,
    User,
    Users
} from '../forms';

export const Mention = BaseZenputModel.extend({
    legacy_classname: '$f.shared.comments.models.Mention',

    defaults: {
        id: null,
        display_name: '',
        tag_in_message: '',
        is_subscriber: null,
        type: 'user'
    }
});

export const Mentions = BaseZenputCollection.extend({
    legacy_classname: '$f.shared.comments.collections.Mentions',

    model: Mention,
    /**
     * Convert this collection of mentions to an array of User()s
     */
    convert_to_users_array: function () {
        return this.map(function (mention) {
            return new User({
                id: mention.id,
                display_name: mention.get('display_name')
            });
        });
    }
});

export const CommentAssociatedUser = BaseZenputModel.extend({
    legacy_classname: '$f.shared.comments.models.CommentAssociatedUser',

    defaults: {
        // requesting user information
        is_subscriber: false,
        has_read: false // whether the user read the comment or not
    }
});

export const CommentsPaginator = Paginator.extend({
    legacy_classname: '$f.shared.comments.models.CommentsPaginator',

    defaults: $.extend({}, Paginator.prototype.defaults, {
        order_by: '-date_created',
        limit: 100
    })
});

export const CommentTarget = BaseZenputModel.extend({
    legacy_classname: '$f.shared.comments.models.Target',

    defaults: {
        id: null, // task or submission_id
        type: null, // 'task' or 'submission'

        date_created: null,
        account: null, // account info (name + id)
        name: null // name of the targetmodel
    },

    submodels: {
        account: Account,
        instance: null // Either BaseTask or FormStructure
    },

    /**
     * Get a list of subscribers for the target.
     *
     * @returns Users - list of users that are subscribed to the target
     */
    get_target_subscribers: function () {
        throw new Error('Programmer Error: get_target_subscribers() not implemented');
    },

    /**
     * Get a list of users for the target.
     *
     * @returns Users - list of users that will be notified for for display
     */
    get_users_to_notify_for_display: function () {
        var recipients = new Users();
        var subscribers = this.get_target_subscribers();
        var user = !$f.mobile ? $f.user : $f.active_user;

        if (!subscribers) {
            return recipients;
        }

        recipients.add(subscribers.toJSON());
        // we won't notify ourselves
        if (recipients.get(user.id)) {
            recipients.remove(recipients.get(user.id));
        }

        return recipients;
    },

    get_users_to_notify_count_for_display: function () {
        return this.get_users_to_notify_for_display().length;
    },
    /**
     * Remove user from target subscribers and the mentioned list, but only if the
     * user is included in the mentioned list.
     *
     * @param User - The user to remove
     * @param Users - The user collection containing currenct comment mentions
     */
    remove_subscribers: function (user_to_remove, mentioned_users) {
        var subscribers = this.get_target_subscribers();
        var was_mentioned_here = mentioned_users.get(user_to_remove);

        if (!was_mentioned_here) {
            return;
        }

        $f.application.vent.trigger('mobile:target:inactive_mentions:remove', user_to_remove);

        mentioned_users.remove(user_to_remove);
        subscribers.remove(user_to_remove);
    },

    /**
     * Add user to target subscribers and the mentioned list, but only if the
     * user was not already a subscriber.
     *
     * @param User - The user to add
     * @param Users - The user collection containing currenct comment mentions
     */
    add_subscribers: function (user_to_add, mentioned_users) {
        var subscribers = this.get_target_subscribers();

        if (subscribers.get(user_to_add)) {
            return;
        }

        mentioned_users.add(user_to_add);
        subscribers.add(user_to_add);

        this._perform_permission_check({
            mentioned_user_id: user_to_add.get('id'),
            success: function (can_user_be_mentioned) {
                if (!can_user_be_mentioned) {
                    subscribers.remove(user_to_add);
                    $f.application.vent.trigger('mobile:target:inactive_mentions:add', user_to_add);
                }
                user_to_add.set('is_subscriber', can_user_be_mentioned);
            },
            error: function () {
                if ($f.mobile) {
                    return $f.mobile.notifications.showErrorMessage();
                }

                // eslint-disable-next-line core-zenput/no-f-web
                new $f.web.views.Notification({
                    type: 'error',
                    heading: 'Error',
                    message: "The user's could not be checked"
                });
            }
        });
    },

    /**
     * Public wrapper around _perform_permission_check for callers outside this module (e.g. React components)
     * that can't reach into underscore-prefixed "private" methods.
     */
    checkMentionPermission: function (options) {
        return this._perform_permission_check(options);
    },

    _perform_permission_check: function (options) {
        options = options || {};
        options.success = options.success || function () {};
        options.error = options.error || function () {};
        var _ajax = $f.utils.select_ajax();

        _ajax({
            url: '/api/v2/comments/mention_permission/' + options.mentioned_user_id,
            dataType: 'json',
            type: 'GET',
            cache: false,
            contentType: 'application/json',
            data: {
                target_type: this.get('type'),
                target_id: this.get('id')
            },
            success: function (response) {
                options.success(response.data.can_be_mentioned);
            },
            error: function (response) {
                options.error();
            }
        });
    },

    toJSON: function (options) {
        return _.omit(this.attributes, 'instance');
    }
});

// May not need these subclasses of target, currently no real difference in the data besides the type enumeration
// but that could change moving forward.
export const CommentTargetForTask = CommentTarget.extend({
    legacy_classname: '$f.shared.comments.models.CommentTargetForTask',

    defaults: _.extend(
        {
            type: 'task'
        },
        CommentTarget.defaults
    ),

    submodels: {
        instance: BaseTask
    },

    get_target_subscribers: function () {
        return this.get('instance').get('subscribers');
    }
});

export const CommentTargetForSubmission = CommentTarget.extend({
    legacy_classname: '$f.shared.comments.models.CommentTargetForSubmission',

    defaults: _.extend(
        {
            type: 'submission'
        },
        CommentTarget.defaults
    ),

    submodels: {
        instance: FormStructure
    },

    get_target_subscribers: function () {
        return this.get('instance').subscribers;
    }
});

export const CommentTargetForFollowUpAction = CommentTarget.extend({
    legacy_classname: '$f.shared.comments.models.CommentTargetForFollowUpAction',

    defaults: _.extend(
        {
            type: 'follow_up_action'
        },
        CommentTarget.defaults
    )
});

export const Comment = BaseZenputModel.extend(
    {
        legacy_classname: '$f.shared.comments.models.Comment',

        defaults: {
            id: null,
            message: null,
            date_created: null,
            date_modified: null,
            created_user: null,
            is_active: true,
            mentions: [],
            user_info: null,
            target: null,
            is_task_submitted: false // this flag to distinguish between a comment & task submission date display
        },

        is_open: false,

        submodels: {
            mentions: Mentions,
            user_info: CommentAssociatedUser,
            target: CommentTarget
        },

        validation: {
            message: { fn: '_validate_message' }
        },

        url: '/api/v2/comments',

        initialize: function (models, options) {
            options = options || {};
            $.extend(this, options);
        },

        sync: function (method, model, options) {
            switch (method) {
                case 'update':
                case 'create':
                    options.url = `${model.url}/comment/`;
                    break;
                case 'delete':
                    options.url = `${model.url}/comment/${model.id}`;
                    break;
            }

            return Backbone.sync(method, model, options);
        },

        _validate_message: function (value, attr, computed_state) {
            if (!value || !value.trim()) {
                return 'Please enter a message.';
            }
        },

        delete: function (options) {
            var this_ = this;
            options = options || {};
            options.success = options.success || function () {};
            options.error = options.error || function () {};
            var _ajax = $f.utils.select_ajax();

            _ajax({
                url: `/api/v2/comments/comment/${this.id}/`,
                dataType: 'json',
                type: 'DELETE',
                cache: false,
                contentType: 'application/json',
                success: function (response) {
                    this_.set('is_active', false);
                    options.success(response);
                },
                error: function (response) {
                    options.error(response);
                }
            });
        },

        decrement_global_comment_count: function () {
            var previous_global_count = $f.mobile.unread_notifications_count.get('count');

            // update global nav count model. The server has new state. Low risk optimistic update.
            if (previous_global_count > 0) {
                $f.mobile.unread_notifications_count.set('count', previous_global_count - 1);
            }
        },

        set_is_open: function (is_open) {
            this.is_open = is_open;

            if (!this.get('user_info').get('has_read')) {
                this.decrement_global_comment_count();
                this.mark_as_read();
            }

            this.trigger('change:is_open', this.is_open);
        },

        get_is_open: function () {
            return this.is_open;
        },

        mark_as_read: function (options) {
            var _ajax = $f.utils.select_ajax();

            var post_data = {
                last_comment_pull_datetime: this.collection.date_last_fetched,
                target: {
                    id: this.get('target').get('id'),
                    type: this.get('target').get('type')
                }
            };

            // be optimistic - for faster ui response, mark as read before http request
            this.get('user_info').set('has_read', true);

            _ajax({
                url: '/api/v2/comments/set_read/',
                dataType: 'json',
                type: 'POST',
                cache: false,
                contentType: 'application/json',
                data: JSON.stringify(post_data),
                success: function (response) {
                    if (options && typeof options.success === 'function') {
                        options.success(response);
                    }
                },
                error: function (response) {
                    if (options && typeof options.error === 'function') {
                        options.error(response);
                    }
                }
            });
        },
        /**
         * Set comment mentions values from the HTML value created by Tribute.
         *
         * @see CommentCreator._init_mentions_typeahead() for the structure of this HTML.
         * @author:david this is arguably 'view' code, but putting in model to allow JS testing
         *
         * @param {string} message_html The HTML comment message containing <span>s of mention values
         */
        set_mentions_from_html: function (message_html) {
            var mentions_data = _.map($(`<p>${message_html}</p>`).find('span'), function (el) {
                return {
                    id: parseInt($(el).attr('data-id')),
                    display_name: $(el).text().substring(1),
                    user_role: {
                        name: $(el).attr('data-user-role')
                    },
                    type: 'user' // @author:david for now, this is all we support
                };
            });

            this.get('mentions').set(mentions_data);
        },

        /**
         * Set the comment message from the HTML value created by Tribute. This will replace all mentions with with our
         * [[[user~id]]] format.
         *
         * @see CommentCreator._init_mentions_typeahead() for the structure of this HTML.
         * @author:david this is arguably 'view' code, but putting in model to allow JS testing
         *
         * @param {string} message_html The HTML comment message containing <span>s of mention values
         */
        set_message_from_html: function (message_html) {
            // replace <br />s with newlines
            message_html = message_html.replace(/<br\s*[\/]?>/gi, '\n');
            message_html = message_html.replace(/<\/p><p>/gi, '\n'); // IE inserts new lines with </p><p>

            var tmp_value = $(`<p>${message_html}</p>`);
            tmp_value.find('span').replaceWith(function (el) {
                return `[[[user~${$(this).attr('data-id')}]]]`;
            });

            this.set('message', tmp_value.text());
        },

        /**
         * Get an HTML formatted message of the comment with mentions filled in and highlighted.
         *
         * @returns string - an HTML string like <a>Display Name</a> or <span data-id="1">Display Name</span>
         */
        get_html_formatted_message: function (options) {
            options = options || {};
            options.disable_clicking = options.disable_clicking || false;
            var this_ = this;
            var message = this.get('message');

            if (!message) {
                return '';
            }

            message = message.replace(/\n/g, '<br />');

            // replace [[[user~#]]] with an <a> or <span>
            return message.replace(/\[\[\[user~([0-9]+)\]\]\]/g, function (found_str, id) {
                var mention = this_.get('mentions').findWhere({ id: parseInt(id) });
                var link = null;

                if (!options.disable_clicking) {
                    link = `<a href="/users/#id/${mention.id}" target="_blank" class="${
                        mention.get('is_subscriber') ? '' : 'mention_inactive'
                    }">@${mention.get('display_name')}</a>`;
                } else {
                    link = `<span target="_blank" class="${
                        mention.get('is_subscriber') ? '' : 'mention_inactive'
                    }">@${mention.get('display_name')}</span>`;
                }

                if (!mention) {
                    return found_str; // shouldn't happen
                }

                if (!options.for_editing) {
                    return link;
                }

                return Comment.get_mention_span({
                    id: mention.id,
                    display: mention.get('display_name'),
                    is_subscriber: mention.get('is_subscriber')
                });
            });
        }
    },
    {
        /**
         * Get a <span>@Mention</span> for our CommentCreator
         *
         * @param {number}  options.id              The mention ID
         * @param {string}  options.display         What should appear on the page
         * @param {string}  options.user_role       The user's role
         * @param {boolean} options.is_subscriber   Whether or not the mentioned user is a subscriber
         */
        get_mention_span: function (options) {
            options = options || {};

            // Because our textarea is actually a p[contenteditable=true] for mentions styling, we need to handle a bug
            // where sometimes a single <br /> exists, even though the user has deleted everything.
            //
            // Also, there is a bug on Android where we can't use contenteditable="false" here, so we'll set data-check-text
            // for our view code to use to verify a correct mention display value and remove it if that's the case.
            // @see @jira:NOTIF-165
            return `<span class="tribute-mention ${options.is_subscriber ? '' : 'mention_inactive'}" data-id="${
                options.id
            }" data-user-role="${options.user_role ? options.user_role : ''}" data-check-text="@${options.display}">@${
                options.display
            }</span>`;
        }
    }
);

export const Comments = BaseZenputCollection.extend({
    legacy_classname: '$f.shared.comments.collections.Comments',

    model: Comment,
    paginator: null, // CommentsPaginator
    target: null, // CommentTarget
    date_last_fetched: null, // date since the last time we fetched comments
    events_added: false, // have we added event "Comments" to the model for display.
    inactive_mentions: null, // $.collections.Users

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);
        var this_ = this;

        if (!this.paginator) {
            this.paginator = new CommentsPaginator();
        }

        this.on('sync', function () {
            this_.date_last_fetched = new Date().toISOString();
            this_.inactive_mentions = this_._get_inactive_mentions();
        });

        this.listenTo($f.application.vent, 'mobile:target:inactive_mentions:add', function (user_to_add) {
            this_.inactive_mentions.add(user_to_add);
        });

        this.listenTo($f.application.vent, 'mobile:target:inactive_mentions:remove', function (user_to_remove) {
            if (!this_.inactive_mentions) {
                return;
            }
            this_.inactive_mentions.remove(user_to_remove);
        });
    },

    /**
     * We either query a target specific list endpoint or the general commments for a user endpoint.
     * @returns {String}
     */
    url: function () {
        return `/api/v2/comments/?target_type=${this.target.get('type')}&target_id=${this.target.get(
            'id'
        )}&${this.paginator.get_query_string({ get_count: true })}`;
    },

    comparator: function (item) {
        return new Date(item.get('date_created')).getTime();
    },

    /**
     *  Only count comments & exclude `activity comments`
     */
    display_length: function () {
        return this.where({ is_task_submitted: false }).length;
    },

    /**
     * Save a new comment and add it to the collection.
     *
     * @param {Comment} new_comment             The comment to save
     * @param {object}                                    options           Callback options
     * @param {function}                                  options.success   The success callback
     * @param {function}                                  options.error     The error callback
     */
    add_comment: function (new_comment, options) {
        options = options || {};
        options.success = options.success || function () {};
        options.error = options.error || function () {};

        var this_ = this;

        new_comment.set('target', this.target);
        new_comment.save(
            {},
            {
                success: function () {
                    this_.add(new_comment);
                    options.success();
                },
                error: function (error) {
                    options.error();
                }
            }
        );
    },

    mark_all_as_read: function (options) {
        var this_ = this;
        options = options || {};
        options.success = options.success || function () {};
        options.error = options.error || function () {};
        var _ajax = $f.utils.select_ajax();

        /* @author:halah when comming to a comments tab from an email,
         * and assuming user just fetching comments for the first time
         * hitting the set_mark route is happening before the date_last_fetched is set
         * and the we get a 400 error. Should we default last_comment_pull_datetime to
         * current date?
         */
        var post_data = { last_comment_pull_datetime: this.date_last_fetched };

        // @author:haroun this target code block may not be relevant. Will a collection of
        // comments have a single target? Not the original author.
        if (this.target) {
            post_data.target = { id: this.target.get('id'), type: this.target.get('type') };
        }

        _ajax({
            url: '/api/v2/comments/set_read/',
            dataType: 'json',
            type: 'POST',
            cache: false,
            contentType: 'application/json',
            data: JSON.stringify(post_data),
            success: function (response) {
                this_.each(function (comment) {
                    if (comment.get('user_info') && !comment.get('user_info').get('has_read')) {
                        comment.get('user_info').set('has_read', true);
                    }
                });
                options.success(response);
            },
            error: function (response) {
                options.error(response);
            }
        });
    },

    has_unread_comments: function () {
        if (this.length === 0) {
            return false;
        }

        if (!this.target.get_target_subscribers()) {
            // get_task_subscribers() may return null if we haven't fetched the full task yet (due to a connection
            // error, etc.). Better to assume we aren't a subscriber until we know for sure.
            return false;
        }

        var active_user_id = $f.mobile ? $f.active_user.id : $f.user.id;

        if (!this.target.get_target_subscribers().get(active_user_id)) {
            return false; // we are not a subscriber, so we shouldn't show unread dot
        }

        return !!_.find(this.models, function (comment) {
            // activity comments such as Task submitted don't have user_info on them, which why we need to check for it
            return comment.get('user_info') && !comment.get('user_info').get('has_read');
        });
    },

    /**
     * Add target based events to the collection for inline display. Currently only task submission date.
     */
    add_event_models: function () {
        if (this.events_added) {
            return;
        }
        if (!this.target || this.target.get('type') !== 'task') {
            return;
        }
        var task = this.target.get('instance');
        if (!task.get('fulfillment') || !task.get('fulfillment').get('date_submitted')) {
            return;
        }

        var event_comment = new Comment({
            date_created: $f.utils.convert_epoch_ts_to_iso_string(task.get('fulfillment').get('date_submitted').$date),
            is_task_submitted: true
        });
        this.add(event_comment);
        this.events_added = true;
    },

    /**
     * Gets Collection of users who've been mention but don't have access to view target comments
     *
     */

    _get_inactive_mentions: function (options) {
        var users = new Users();

        this.each(function (comment) {
            var mentions = comment.get('mentions');

            mentions.each(function (mentioned_user) {
                if (!mentioned_user.get('is_subscriber')) {
                    users.add(mentioned_user);
                }
            });
        });

        return users;
    }
});

export const UnreadCount = BaseZenputModel.extend({
    legacy_classname: '$f.shared.comments.models.UnreadCount',

    defaults: {
        count: 0
    },

    url: '/api/v2/comments/user_unread_count/?include_target_types=announcement,submission,task',

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);

        this.on('change', async () => {
            if ($f.mobile.is.native() && $f.mobile.is.ios()) {
                // Because this file is symlinked between the webapp and mobile
                // we can't import our CordovaPlugins module. Instead, we'll
                // use the window to access Pushwoosh directly until we fully separate
                // mobile from web.
                const Pushwoosh = window.cordova.require('pushwoosh-cordova-plugin.PushNotification');
                Pushwoosh.setApplicationIconBadgeNumber(this.get('count'));
            }
        });
    },

    reset: function () {
        this.set('count', 0, { silent: true });
        this.trigger('change', this); // manual trigger may be necessary when count is already 0
    }
});
